/**
 * T13: Detail-Ansicht für 3g-Excel-Import-Karten (expenses, kind='excel_import',
 * Migration 041; Daten-Vertrag: hub/src/lib/xlsx-import.ts baueImportDaten →
 * data.{source:'excel', filename, rows, summen, nicht_exportierbar}).
 *
 * Erbt (2026-07-13) die gemeinsame Detail-Basis: DetailViewShell (Kopf + GLEICH
 * HOHE Zwei-Panel + CardDetailActions-Footer) und useCellHighlight (bidirektionales
 * Quelle↔Summen-Highlight + Klick-zum-Springen). Es gibt keinen Scan (pdf_path
 * leer, .xlsx-Quelle) — die „Quelle" ist der eingelesene Excel-Inhalt (die Roh-
 * Zeilen), damit man die Summen wie beim PDF gegen die Quelle daneben prüfen kann.
 * Rechts: Summen je Mitarbeiter × Lohnart (das, was in den RZL-Export geht).
 * nicht_exportierbar-Arten sind importiert, aber im RZL-Export nicht abbildbar —
 * gelber Hinweis, nicht stilles Weglassen.
 */
import { useState } from "react";
import type { Employee, ExcelImportData, ExcelImportRow, ExcelImportSumme } from "../lib/api";
import { formatEuro } from "../lib/zulagen-render";
import { DetailViewShell, CardIdChip } from "./DetailViewShell";
import { CardDetailActions } from "./CardDetailActions";
import { useCellHighlight } from "../lib/useCellHighlight";
import {
  analyzeDoppelLohnart, doppelRegelTooltip,
  DOPPEL_REGEL_BADGE, DOPPEL_PROBLEM_BADGE,
  type DoppelItem, type DoppelRegeln,
} from "../lib/doppel-lohnart";

/** Anzeige-Name eines Summen-/Roh-Zeilen-Mitarbeiters: Stammdaten-Name wenn
 *  auflösbar, sonst die DN als fail-loud-sichtbarer Fallback. */
function employeeName(employees: Employee[], mitarbeiterId: string, dn: string): string {
  const emp = employees.find((e) => e.id === mitarbeiterId);
  return emp ? `${emp.last_name}, ${emp.first_name}` : `DN ${dn}`;
}

/** Anzeige einer Lohnart: Zahl oder String aus der Matrix; null = nicht
 *  exportierbar (kein RZL-Mapping). */
function lohnartLabel(lohnart: ExcelImportSumme["lohnart"]): string {
  return lohnart === null || lohnart === undefined ? "— (nicht exportierbar)" : String(lohnart);
}

/** Aktive Auswahl: eine Roh-Zeile (Index) ODER eine Summen-Gruppe (Index).
 *  Die fachliche Zuordnung (welche Zeile in welche Gruppe fließt) bleibt hier
 *  als Prädikat — der Hook liefert nur active/setActive + das Klick-Springen. */
type ExcelActive = { kind: "group" | "row"; idx: number };

export function ExcelImportDetail({
  cardId,
  month,
  state,
  clientId,
  data,
  employees,
  employeeId,
  modal,
  onBack,
  onRefresh,
  doppelLohnartRegeln,
}: {
  cardId: string;
  month: string;
  state: string;
  /** Für die CardDetailActions-Footer-Leiste (Export/Revert). */
  clientId?: string;
  data: Partial<ExcelImportData>;
  employees: Employee[];
  /** Zugeordneter Mitarbeiter der KARTE (Bug 1, Thomas 24.07.): seit dem
   *  MA×Typ-Split gehört jede Excel-Karte genau EINEM Mitarbeiter — die
   *  Zuordnung ist sicht- und änderbar wie bei den PDF-Karten. */
  employeeId?: string | null;
  modal?: boolean;
  onBack: () => void;
  /** Karte nach einer State-Aktion neu laden (Parent re-fetcht + re-rendert). */
  onRefresh?: () => Promise<void>;
  /** excel_art → erwartete Lohnarten (aus der Mandant-Matrix), nur Doppel-
   *  Buchungen (≥2 Lohnarten). Quelle für den „unvollständig"-Flag (T29). Ohne
   *  diese Regeln funktionieren Einrückung/Badge/Inkonsistenz weiter, nur der
   *  „fehlt-ein-Partner"-Check entfällt (fail-soft). */
  doppelLohnartRegeln?: DoppelRegeln;
}) {
  const rows = Array.isArray(data.rows) ? data.rows : [];
  const summen = Array.isArray(data.summen) ? data.summen : [];
  const nichtExportierbar = Array.isArray(data.nicht_exportierbar) ? data.nicht_exportierbar : [];
  // T28 (Thomas 13.07.): berechneter Prozessschritt → wie die Stundenlisten-
  // Karte zeigt auch diese Karte die „Berechnung" (calc_model + angewandte
  // Regel). data.calculation ist das Ergebnis des calc-Schritts (bei Excel 1:1
  // aus den Import-Summen, Quellen bleiben getrennt).
  const calc = (data as { calculation?: { kind?: string; rule?: string } }).calculation;
  const kostenGesamt = summen.reduce((acc, s) => acc + (Number.isFinite(s.kosten) ? s.kosten : 0), 0);
  const mitarbeiterCount = new Set(summen.map((s) => s.mitarbeiter_id)).size;

  // T29/T30: Doppel-Lohnart-Analyse über den geteilten Helper analyzeDoppelLohnart.
  // Die abgeleitete (regel-erzeugte) Lohnart wird eingerückt + „Regel"-Badge; kaputte
  // Paare (unvollständig/inkonsistent) werden geflaggt. primary/derived kommt HIER aus
  // den QUELL-Zeilen (data.rows tragen die Lohnart): was dort steht = primär, der Rest
  // ist regel-abgeleitet.
  const gKey = (mid: string, arten: string[]): string => `${mid}|${[...arten].sort().join("|")}`;
  const quellByGroup = new Map<string, Set<string>>();
  for (const r of rows) {
    if (r.lohnart === null || r.lohnart === undefined) continue;
    const k = gKey(r.mitarbeiter_id, [r.excel_art]);
    let set = quellByGroup.get(k);
    if (!set) { set = new Set<string>(); quellByGroup.set(k, set); }
    set.add(String(r.lohnart));
  }
  const regeln = doppelLohnartRegeln ?? {};
  const doppelItems: DoppelItem[] = summen.map((s) => ({
    groupKey: gKey(s.mitarbeiter_id, s.excel_arten),
    art: s.excel_arten.find((a) => regeln[a]) ?? s.excel_arten[0] ?? "",
    lohnart: s.lohnart,
    anzahl: s.anzahl,
    basis: s.kosten,
  }));
  const infos = analyzeDoppelLohnart(doppelItems, regeln, {
    quellLohnarten: (k) => quellByGroup.get(k) ?? new Set<string>(),
  });
  const hasDoppel = infos.some((i) => i.isDoppel);

  // Lokaler busy/err-State für die geerbte CardDetailActions-Leiste.
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Bug 1 (Thomas 24.07., vereinbart: Excel-Karte "identisch zur PDF-Auto-
  // Karte"): in editierbaren Zuständen sind MA-Zuordnung und Anzahl je
  // Summen-Gruppe ÄNDERBAR (PATCH /expenses/:id — server-side merge mit
  // Korrektur-Provenance), danach Validieren wie bei jeder anderen Karte.
  const editierbar = state === "ocr_complete" || state === "pending_review";
  const [anzahlEdit, setAnzahlEdit] = useState<Record<number, string>>({});
  const summenDirty = Object.keys(anzahlEdit).length > 0;
  const patchCard = async (patch: { employee_id?: string | null; data?: unknown }) => {
    setBusy(true);
    setErr(null);
    try {
      const r = await fetch(new URL(`api/expenses/${cardId}`, document.baseURI).toString(), {
        method: "PATCH",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (!r.ok) throw new Error(`${r.status}: ${await r.text()}`);
      await onRefresh?.();
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };
  const saveAnzahl = async () => {
    const neu = summen.map((s, j) => {
      const roh = anzahlEdit[j];
      if (roh === undefined) return s;
      const wert = Number(String(roh).replace(",", "."));
      return Number.isFinite(wert) ? { ...s, anzahl: wert } : s;
    });
    await patchCard({ data: { summen: neu } });
    setAnzahlEdit({});
  };

  // T21 + Vererbung (2026-07-13): bidirektionales Highlight Quelle ↔ Summen über
  // den geteilten useCellHighlight-Hook. Hover einer Summen-Gruppe (MA × Lohnart)
  // rechts hebt links die Roh-Zeilen hervor, die in sie eingeflossen sind
  // (gleicher mitarbeiter_id UND excel_art in dieser Gruppe) — und umgekehrt.
  // NEU: Klick springt zum Gegenstück — klick auf eine Summen-Gruppe scrollt die
  // zugehörigen (evtl. weit unten liegenden) Quell-Zeilen in den Sichtbereich und
  // pulst sie (Thomas' Off-Screen-Beschwerde). Eine Roh-Zeile kann in ZWEI Gruppen
  // fallen (Doppel-Lohnart 13+14, gleiche excel_art), daher prädikat-basiert.
  const highlight = useCellHighlight<ExcelActive>();
  const { active, setActive, linkProps } = highlight;
  const rowInGroup = (r: ExcelImportRow, g: ExcelImportSumme): boolean =>
    r.mitarbeiter_id === g.mitarbeiter_id && g.excel_arten.includes(r.excel_art);
  // Match-Tokens: Gruppe j = "g{j}"; eine Roh-Zeile trägt die Tokens aller
  // Gruppen, in die sie fällt → Klick-Springen findet exakt die Gegenstücke.
  const groupTokensForRow = (i: number): string[] =>
    summen.map((g, j) => (rowInGroup(rows[i]!, g) ? `g${j}` : null)).filter((x): x is string => x !== null);
  const rowActive = (i: number): boolean => {
    if (!active) return false;
    if (active.kind === "row") return active.idx === i;
    const g = summen[active.idx];
    const r = rows[i];
    return !!g && !!r && rowInGroup(r, g);
  };
  const groupActive = (j: number): boolean => {
    if (!active) return false;
    if (active.kind === "group") return active.idx === j;
    const r = rows[active.idx];
    const g = summen[j];
    return !!r && !!g && rowInGroup(r, g);
  };
  // Highlight erhält die gelbe „nicht exportierbar“-Semantik: gelb bleibt gelb
  // (nur kräftiger), sonst hellblau — beides transient beim Hover.
  const hiStyle = (isYellow: boolean, isHi: boolean): React.CSSProperties => ({
    ...(isYellow ? { background: "#fef9c3" } : {}),
    ...(isHi ? { background: isYellow ? "#fde68a" : "#dbeafe" } : {}),
    cursor: "pointer",
  });

  const title = (
    <>
      Excel-Import · {month}
      <span style={{
        marginLeft: 12, fontSize: 13, fontWeight: 600,
        padding: "2px 8px", borderRadius: 4,
        background: "#dcfce7", color: "#166534",
        border: "1px solid #86efac",
      }} title="Karte aus einem Excel-Report importiert — kein PDF/Scan vorhanden">
        Excel-Import
      </span>
    </>
  );

  const source = (
    <>
      <strong>Quelle:</strong>{" "}
      {/* T24b: echter Download-Link auf die gespeicherte Original-xlsx —
          analog zum Bestandsmandant (ExpenseDetail verlinkt das Quell-PDF).
          Nur wenn die Bytes gespeichert sind (Karten seit T24b); ältere
          Karten zeigen nur den Dateinamen ohne Link. */}
      {data.source_file?.stored ? (
        <a
          href={new URL(`api/expenses/${cardId}/source`, document.baseURI).toString()}
          title="Original-Excel herunterladen"
          style={{ color: "#1d4ed8" }}
        >
          {data.filename ?? data.source_file.filename ?? "Original-Excel"}
        </a>
      ) : (
        <code>{data.filename ?? "(Dateiname fehlt)"}</code>
      )}
      {typeof data.source_file?.bytes === "number" && (
        <span> · {(data.source_file.bytes / 1024).toFixed(0)} KB</span>
      )}
      <span> · {rows.length} Zeilen importiert</span>
      <span> · <CardIdChip id={cardId} /></span>
    </>
  );

  const meta = (
    <>
      {/* Bug 1: MA-Zuordnung der Karte sicht- und (in editierbaren Zuständen)
          änderbar — wie das MA-Select der PDF-Karten. Ohne Zuordnung sperrt
          der Validate-Endpoint (400 employee not assigned). */}
      <span>
        Mitarbeiter:{" "}
        {editierbar ? (
          <select
            value={employeeId ?? ""}
            disabled={busy}
            onChange={(e) => void patchCard({ employee_id: e.target.value || null })}
            style={{ fontSize: 12 }}
          >
            <option value="">— zuordnen —</option>
            {employees.map((e) => (
              <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>
            ))}
          </select>
        ) : (
          <strong>
            {(() => {
              const emp = employees.find((e) => e.id === employeeId);
              return emp ? `${emp.first_name} ${emp.last_name}` : "—";
            })()}
          </strong>
        )}
      </span>
      <span>Kosten gesamt: <strong>{formatEuro(kostenGesamt)}</strong></span>
      <span className="ts-state">Status: <strong>{state}</strong></span>
    </>
  );

  const notices = nichtExportierbar.length > 0 ? (
    <div className="warning-box">
      <strong>{nichtExportierbar.length} Zulagen-Art{nichtExportierbar.length === 1 ? "" : "en"} nicht
      exportierbar</strong> (importiert, aber ohne RZL-Lohnart — fehlt im Export):{" "}
      {nichtExportierbar.map((art, i) => (
        <span key={art}>
          {i > 0 && ", "}
          <code>{art}</code>
        </span>
      ))}
    </div>
  ) : null;

  // LINKS: die eingelesenen Original-Zeilen (die Quelle zum Abgleich).
  const left = (
    <div style={{ padding: 12 }}>
      <h3 style={{ margin: "0 0 6px", fontSize: 15 }}>Quelle · {data.filename ?? "Excel"}</h3>
      <p className="muted" style={{ marginTop: 0, marginBottom: 8, fontSize: 12 }}>
        Die eingelesenen Original-Zeilen ({rows.length}) — die Quelle zum Abgleich
        gegen die Summen rechts.{" "}
        {data.source_file?.stored
          ? "Die Original-Excel ist über den Quelle-Link oben herunterladbar."
          : "Angezeigt ist der importierte Inhalt (Original-Datei nicht gespeichert)."}
      </p>
      <p className="muted" style={{ marginTop: 0, marginBottom: 10, fontSize: 11 }}>
        <span style={{
          background: "#dbeafe", border: "1px solid #3b82f6", borderRadius: 3,
          padding: "0 5px", marginRight: 6,
        }}>blau</span>
        Über eine <strong>Summen-Zeile rechts</strong> fahren hebt hier die zugehörigen
        Quell-Zeilen hervor. <strong>Klick</strong> auf eine Summen-Zeile scrollt sie
        hierher und pulst sie — und umgekehrt (gleicher Mitarbeiter × Zulagenart).
      </p>
      <table className="ts-table" style={{ fontSize: 11 }}>
        <thead>
          <tr>
            <th>#</th>
            <th>Mitarbeiter</th>
            <th>DN</th>
            <th>Art (Excel)</th>
            <th>Lohnart</th>
            <th>Datum</th>
            <th>Einheit</th>
            <th style={{ textAlign: "right" }}>EP</th>
            <th style={{ textAlign: "right" }}>Anzahl</th>
            <th style={{ textAlign: "right" }}>Kosten</th>
            <th>Projekt</th>
            <th>Kostenstelle</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={i}
              {...linkProps({ side: "source", key: { kind: "row", idx: i }, match: groupTokensForRow(i) })}
              style={hiStyle(r.lohnart === null, rowActive(i))}
              title="Klick: zugehörige Summen-Gruppe(n) rechts hervorheben"
            >
              <td className="muted">{i + 1}</td>
              <td>{employeeName(employees, r.mitarbeiter_id, r.dn)}</td>
              <td>{r.dn}</td>
              <td title={r.art !== r.excel_art ? `Original: ${r.art}` : undefined}>{r.excel_art}</td>
              <td>{lohnartLabel(r.lohnart)}</td>
              <td>{r.datum}</td>
              <td>{r.einheit}</td>
              <td style={{ textAlign: "right" }}>{String(r.ep).replace(".", ",")}</td>
              <td style={{ textAlign: "right" }}>{String(r.anzahl).replace(".", ",")}</td>
              <td style={{ textAlign: "right" }}>{formatEuro(r.kosten)}</td>
              <td>{r.projekt}</td>
              <td>{r.kostenstelle}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // RECHTS: die Berechnung (Summen je MA × Lohnart) = die Export-Vorschau.
  const right = (
    <div style={{ padding: 12 }}>
      <h3 style={{ margin: "0 0 6px", fontSize: 15 }}>Berechnung · was in den RZL-Export geht</h3>
      {/* T28: „Details was berechnet wurde" wie bei der Stundenlisten-Karte
          (calc_model + angewandte Regel), dann das Ergebnis (Summen je
          MA × Lohnart = die Export-Vorschau). */}
      {calc && (
        <dl style={{
          margin: "0 0 10px", display: "grid", gridTemplateColumns: "auto 1fr",
          gap: "3px 12px", fontSize: 12, background: "#f8fafc",
          border: "1px solid #e2e8f0", borderRadius: 4, padding: "8px 10px",
        }}>
          <dt style={{ fontWeight: 600, color: "#475569" }}>Berechnungsmodell</dt>
          <dd style={{ margin: 0 }}><code>{calc.kind ?? "—"}</code></dd>
          <dt style={{ fontWeight: 600, color: "#475569" }}>Regel</dt>
          <dd style={{ margin: 0 }}>{calc.rule ?? "—"}</dd>
        </dl>
      )}
      <p className="muted" style={{ marginTop: 0, marginBottom: 8, fontSize: 12 }}>
        Ergebnis der Berechnung: pro Mitarbeiter und RZL-Lohnart aufsummierte
        Anzahl und Kosten — genau das geht in den RZL-Export.
      </p>
      <div style={{ marginBottom: 12, fontSize: 12 }}>
        <span style={{
          background: "#fef9c3", border: "1px solid #eab308", borderRadius: 3,
          padding: "0 5px", marginRight: 6,
        }}>gelb</span>
        = keine RZL-Lohnart (importiert, aber nicht exportierbar — fehlt im Export).
      </div>
      {hasDoppel && (
        <div style={{ marginBottom: 12, fontSize: 12, lineHeight: 1.5 }}>
          <span style={{ color: "#475569", marginRight: 4 }}>↳</span>
          eingerückte Zeile mit <span style={{ ...DOPPEL_REGEL_BADGE, marginLeft: 0 }}>Regel ⓘ</span>
          {" "}= regel-erzeugte Lohnart (steht nicht in der Quelle; z. B. Baustellenzulage → 13 aus
          der Quelle, 14 per Regel; gleiche Basis, SV-Splittung macht RZL — keine Doppelzahlung).
          {"  "}<span style={{ ...DOPPEL_PROBLEM_BADGE, marginLeft: 0 }}>⚠</span>
          {" "}= kaputtes Paar (unvollständig / inkonsistent) — das Warum steht im Hover.
        </div>
      )}
      {summen.length === 0 ? (
        <div style={{
          background: "#fef2f2", border: "1px solid #dc2626", borderLeft: "4px solid #dc2626",
          padding: 12, borderRadius: 4, fontSize: 13, color: "#7f1d1d",
        }}>
          Keine Summen auf der Karte — der Import hat keine Daten hinterlassen.
          Das ist ein Fehler (Import legt Karten nur mit Summen an), bitte den
          Import prüfen.
        </div>
      ) : (
        <table className="ts-table" style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>Mitarbeiter</th>
              <th>DN</th>
              <th>Excel-Arten</th>
              <th>Lohnart</th>
              <th style={{ textAlign: "right" }}>Anzahl</th>
              <th style={{ textAlign: "right" }}>Kosten</th>
              <th style={{ textAlign: "right" }} title="Anzahl Roh-Zeilen in dieser Gruppe">Zeilen</th>
            </tr>
          </thead>
          <tbody>
            {summen.map((s, j) => {
              const info = infos[j]!;
              return (
              <tr
                key={`${s.mitarbeiter_id}-${String(s.lohnart)}-${j}`}
                {...linkProps({ side: "value", key: { kind: "group", idx: j }, match: [`g${j}`] })}
                style={{
                  ...hiStyle(s.lohnart === null, groupActive(j)),
                  ...(info.problem ? { background: "#fef2f2" } : {}),
                }}
                title={s.lohnart === null
                  ? "Keine RZL-Lohnart — fehlt im Export · Klick: Quell-Zeilen links hervorheben"
                  : "Klick: zugehörige Quell-Zeilen links hervorheben"}
              >
                <td>{employeeName(employees, s.mitarbeiter_id, s.dn)}</td>
                <td>{s.dn}</td>
                <td>{s.excel_arten.join(", ")}</td>
                <td>
                  {info.isAbgeleitet
                    ? <span style={{ paddingLeft: 12, color: "#475569" }}>↳ {lohnartLabel(s.lohnart)}</span>
                    : lohnartLabel(s.lohnart)}
                  {info.isDoppel && info.isAbgeleitet && (
                    <span style={DOPPEL_REGEL_BADGE} title={doppelRegelTooltip(s.excel_arten.join(", "), info.partner)}>Regel ⓘ</span>
                  )}
                  {info.problem && (
                    <span style={DOPPEL_PROBLEM_BADGE} title={info.problem.warum}>⚠ {info.problem.kurz}</span>
                  )}
                </td>
                <td style={{ textAlign: "right" }}>
                  {editierbar ? (
                    <input
                      value={anzahlEdit[j] ?? String(s.anzahl).replace(".", ",")}
                      onChange={(e) => setAnzahlEdit((p) => ({ ...p, [j]: e.target.value }))}
                      onClick={(e) => e.stopPropagation()}
                      style={{ width: 64, textAlign: "right", fontSize: 12 }}
                      title="Anzahl ändern — Speichern-Knopf unten in der Leiste"
                    />
                  ) : String(s.anzahl).replace(".", ",")}
                </td>
                <td style={{ textAlign: "right" }}>{formatEuro(s.kosten)}</td>
                <td style={{ textAlign: "right" }}>{s.zeilen}</td>
              </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: "2px solid #94a3b8", background: "#f8fafc", fontWeight: 700 }}>
              <td colSpan={5}>Σ</td>
              <td style={{ textAlign: "right" }}>{formatEuro(kostenGesamt)}</td>
              <td style={{ textAlign: "right" }}>{rows.length}</td>
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  );

  // Geerbte Standard-Aktionen (Reopen/Export/Re-Export/Revert „↻ Zurück zur
  // Bearbeitung") aus der kind-agnostischen CardDetailActions-Leiste — nur wenn
  // der Parent onRefresh liefert (State-Aktionen re-fetchen die Karte).
  const actions = onRefresh ? (
    <CardDetailActions
      kind="expense"
      cardId={cardId}
      month={month}
      clientId={clientId}
      state={state}
      busy={busy}
      err={err}
      setBusy={setBusy}
      setErr={setErr}
      onRefresh={onRefresh}
      allowInlineValidate={true}
      hideReOcr={true}
      inlineValidateDisabledReason={
        !employeeId ? "Erst Mitarbeiter zuordnen (oben)"
        : summenDirty ? "Erst die geänderten Anzahlen speichern (Leiste unten)"
        : undefined
      }
    />
  ) : null;

  const speichernLeiste = editierbar && summenDirty ? (
    <div style={{ padding: "8px 12px", borderTop: "1px solid #e2e8f0" }}>
      <button className="btn" disabled={busy} onClick={() => void saveAnzahl()}>
        Änderungen speichern
      </button>
      <button
        className="btn"
        style={{ marginLeft: 8 }}
        disabled={busy}
        onClick={() => setAnzahlEdit({})}
      >
        Verwerfen
      </button>
    </div>
  ) : null;

  return (
    <DetailViewShell
      modal={modal}
      onBack={onBack}
      title={title}
      source={source}
      meta={meta}
      notices={notices}
      left={left}
      right={right}
      highlight={highlight}
      actions={
        <>
          {speichernLeiste}
          {actions}
        </>
      }
    />
  );
}
